import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { StaffContext, useStaffGate } from '../../lib/staffContext';
import { ContextError } from '../../ui/ContextError';
import { StaffShell } from '../../ui/StaffShell';
import { useI18n } from '../../i18n';

export type TeacherNav = 'classes' | 'calibration' | 'playbook';

// The teacher's frame: active-role gate (teacher only), sidebar nav and the
// staff shell. Every teacher page renders inside it.
export function TeacherFrame({
  title,
  active,
  children,
  actions,
}: {
  title: string;
  active: TeacherNav | null;
  children: (ctx: StaffContext) => ReactNode;
  actions?: ReactNode;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { ctx, error } = useStaffGate(['teacher']);

  if (error) return <ContextError message={error} />;
  if (!ctx) return <main className="page-loading" role="status">{t.loading}</main>;

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  return (
    <StaffShell
      role={t['role.teacher']}
      nav={[
        { icon: '👥', label: t['teacher.title'], href: '/teacher', active: active === 'classes' },
        { icon: '🎯', label: t['ts.navCalibration'], href: '/teacher/calibration', active: active === 'calibration' },
        { icon: '📘', label: t['h2.playbookTitle'], href: '/teacher/playbook', active: active === 'playbook' },
        { icon: '🫶', label: t['r3.me.nav'], href: '/me/checkin' },
      ]}
      title={title}
      who={ctx.display_name}
      ctx={ctx}
      onSignOut={signOut}
      actions={actions}
    >
      {children(ctx)}
    </StaffShell>
  );
}
